import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useP } from '../../i18n/useP';
import { listHrContracts, getPeopleEvolutionReading } from '../../api/hr';
import EvolutionReadingCard from './EvolutionReadingCard';
import HeroCard from './HeroCard';
import KpiTile from './KpiTile';
import EvolutionChart from './EvolutionChart';
import { buildEvolutionModel, buildEvolutionSeries } from './evolutionModel';
import PageHeader from '../../components/PageHeader';
import './HrPage.css';

export default function HrEvolutionPage({ activeTenant, year, onYearChange, embedded, unit = 'chf' }) {
  const { t } = useTranslation();
  const p = useP();
  const tenantId = activeTenant?.id;
  const refYear = year || new Date().getFullYear();
  const prevYear = refYear - 1;

  const [contracts, setContracts] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [reading, setReading] = useState({ patterns: [], loading: false, error: null });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!tenantId) return;
    setLoading(true);
    setReading({ patterns: [], loading: true, error: null });
    Promise.all([
      listHrContracts(tenantId),
      getPeopleEvolutionReading(tenantId, refYear),
    ])
      .then(([c, r]) => {
        if (!c.error) setContracts(c.data || []);
        if (r && !r.error && r.data) {
          setMetrics(r.data.metrics || null);
          setReading({ patterns: r.data.readings?.patterns || [], loading: false, error: null });
        } else {
          setReading({ patterns: [], loading: false, error: r?.error?.message || null });
        }
      })
      .finally(() => setLoading(false));
  }, [tenantId, refYear]);

  const model = useMemo(() => buildEvolutionModel(metrics, prevYear, refYear, unit), [metrics, prevYear, refYear, unit]);
  const series = useMemo(
    () => buildEvolutionSeries(
      contracts, prevYear, refYear, model.csRateN,
      metrics?.monthly_mass_n_1, metrics?.monthly_mass_n,
    ),
    [contracts, prevYear, refYear, model.csRateN, metrics],
  );

  if (!tenantId) return <main className="hr-page"><div className="hr-page__empty">{p('Sélectionner une entreprise.')}</div></main>;

  return (
    <main className={`hr-page${embedded ? ' is-embedded' : ''}`}>
      {!embedded && (
        <PageHeader
          title={p('Evolution masse salariale')}
        />
      )}

      {loading && <div className="hr-page__loading">{t('common:status.loading')}</div>}
      {!loading && metrics && (
        <>
          <HeroCard {...model.hero} bridge={model.bridge} />

          <div className="hr-synth hr-synth--flat" style={{ marginBottom: 14 }}>
            <div className="hr-synth__head">{p('INDICATEURS · ')}{prevYear} → {refYear}</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 8 }}>
              {model.tiles.map((tile, i) => (
                <KpiTile key={i} prevYear={prevYear} {...tile} />
              ))}
            </div>
          </div>

          <EvolutionChart {...series} prevYear={prevYear} refYear={refYear} />

          <div style={{ marginTop: 14 }}>
            <EvolutionReadingCard
              patterns={reading.patterns}
              loading={reading.loading}
              error={reading.error}
            />
          </div>
        </>
      )}
    </main>
  );
}
